import React from 'react';
import { Box, Paper, Typography, Tooltip, Chip, Avatar } from '@mui/material';

function AvatarCard({ avatar, owned = false, selected = false, onClick, hideName = false, currentFrame = null }) {
  const rarityColors = {
    R: { bg: 'rgba(33, 150, 243, 0.2)', border: 'rgba(33, 150, 243, 0.5)', label: 'R' },
    SR: { bg: 'rgba(156, 39, 176, 0.2)', border: 'rgba(156, 39, 176, 0.5)', label: 'SR' },
    SSR: { bg: 'rgba(255, 152, 0, 0.2)', border: 'rgba(255, 152, 0, 0.5)', label: 'SSR' }
  };

  const color = rarityColors[avatar.rarity] || rarityColors.R;

  // 20种霓虹色 - 手动定义，与 AvatarFrameCard 和 UserAvatar 保持一致
  const neonColors = [
    { r: 255, g: 20, b: 147 },    // 0: 霓虹粉
    { r: 0, g: 255, b: 127 },     // 1: 霓虹绿
    { r: 0, g: 191, b: 255 },     // 2: 霓虹蓝
    { r: 255, g: 215, b: 0 },     // 3: 霓虹金
    { r: 255, g: 20, b: 20 },     // 4: 霓虹红
    { r: 138, g: 43, b: 226 },    // 5: 霓虹紫
    { r: 0, g: 255, b: 255 },     // 6: 霓虹青
    { r: 255, g: 105, b: 180 },   // 7: 霓虹粉红
    { r: 50, g: 205, b: 50 },     // 8: 霓虹青绿
    { r: 30, g: 144, b: 255 },    // 9: 霓虹天蓝
    { r: 255, g: 140, b: 0 },     // 10: 霓虹橙
    { r: 186, g: 85, b: 211 },    // 11: 霓虹紫罗兰
    { r: 64, g: 224, b: 208 },    // 12: 霓虹青绿
    { r: 255, g: 69, b: 0 },      // 13: 霓虹红橙
    { r: 147, g: 112, b: 219 },   // 14: 霓虹中紫
    { r: 0, g: 250, b: 154 },     // 15: 霓虹薄荷绿
    { r: 255, g: 0, b: 255 },     // 16: 霓虹洋红
    { r: 32, g: 178, b: 170 },    // 17: 霓虹浅海绿
    { r: 255, g: 165, b: 0 },     // 18: 霓虹橙
    { r: 148, g: 0, b: 211 }      // 19: 霓虹紫
  ];

  // Get neon color for frame based on badge count
  const getNeonColor = (badgeCount) => {
    const index = Math.min(badgeCount, 19);
    return neonColors[index];
  };

  // Generate frame style if currentFrame is provided
  const getFrameStyle = () => {
    if (!currentFrame) return {};
    
    const badgeCount = currentFrame.requiredBadges || 0;
    const color = getNeonColor(badgeCount);
    const rgbString = `rgb(${color.r}, ${color.g}, ${color.b})`;
    const rgbaString = (alpha) => `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
    
    const borderWidth = 2 + Math.floor(badgeCount / 5);
    const shadowIntensity = 0.3 + (badgeCount * 0.025);
    const shadowSize = 4 + (badgeCount * 0.8);
    
    if (badgeCount < 5) {
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}`,
        backgroundColor: 'transparent'
      };
    } else if (badgeCount < 10) {
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, inset 0 0 ${shadowSize * 0.6}px ${rgbaString(0.1)}`,
        backgroundImage: `linear-gradient(135deg, ${rgbaString(0.1)}, ${rgbaString(0.15)})`,
        backgroundColor: 'transparent'
      };
    } else if (badgeCount < 15) {
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, 0 0 ${shadowSize * 1.5}px ${rgbaString(shadowIntensity * 0.5)}, inset 0 0 ${shadowSize * 0.7}px ${rgbaString(0.15)}`,
        backgroundImage: `linear-gradient(135deg, ${rgbaString(0.15)}, ${rgbaString(0.25)}, ${rgbaString(0.15)})`,
        backgroundColor: 'transparent',
        backgroundSize: '200% 200%',
        '@keyframes gradientShift': {
          '0%, 100%': {
            backgroundPosition: '0% 50%'
          },
          '50%': {
            backgroundPosition: '100% 50%'
          }
        },
        animation: 'gradientShift 3s ease infinite'
      };
    } else {
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, 0 0 ${shadowSize * 2}px ${rgbaString(shadowIntensity * 0.6)}, 0 0 ${shadowSize * 3}px ${rgbaString(shadowIntensity * 0.3)}, inset 0 0 ${shadowSize}px ${rgbaString(0.2)}`,
        backgroundImage: `linear-gradient(135deg, ${rgbaString(0.2)}, ${rgbaString(0.3)}, ${rgbaString(0.2)})`,
        backgroundColor: 'transparent',
        backgroundSize: '200% 200%',
        '@keyframes pulse': {
          '0%, 100%': {
            boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, 0 0 ${shadowSize * 2}px ${rgbaString(shadowIntensity * 0.6)}, 0 0 ${shadowSize * 3}px ${rgbaString(shadowIntensity * 0.3)}`
          },
          '50%': {
            boxShadow: `0 0 ${shadowSize * 1.3}px ${rgbaString(shadowIntensity * 1.2)}, 0 0 ${shadowSize * 2.5}px ${rgbaString(shadowIntensity * 0.8)}, 0 0 ${shadowSize * 4}px ${rgbaString(shadowIntensity * 0.5)}`
          }
        },
        '@keyframes gradientShift': {
          '0%, 100%': {
            backgroundPosition: '0% 50%'
          },
          '50%': {
            backgroundPosition: '100% 50%'
          }
        },
        animation: 'pulse 2s ease-in-out infinite, gradientShift 3s ease infinite'
      };
    }
  };

  const frameStyle = currentFrame ? getFrameStyle() : {};

  return (
    <Tooltip title={avatar.name} arrow>
      <Paper
        onClick={owned ? onClick : undefined}
        sx={{
          p: 1,
          textAlign: 'center',
          backgroundColor: selected 
            ? color.bg 
            : owned 
              ? 'rgba(255, 255, 255, 0.05)' 
              : 'rgba(255, 255, 255, 0.02)',
          border: `2px solid ${selected ? color.border : owned ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'}`,
          borderRadius: 2,
          opacity: owned ? 1 : 0.4,
          transition: 'all 0.3s',
          cursor: owned ? 'pointer' : 'default',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: owned ? 'translateY(-4px)' : 'none',
            boxShadow: owned ? 4 : 2,
            borderColor: color.border,
            backgroundColor: owned 
              ? color.bg 
              : 'rgba(255, 255, 255, 0.05)',
            opacity: 1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${color.bg}40, transparent)`,
              pointerEvents: 'none',
              zIndex: 0
            }
          }
        }}
      >
        {selected && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <Typography variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>✓</Typography>
          </Box>
        )}
        
        <Box
          sx={{
            width: '100%',
            aspectRatio: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            position: 'relative',
            filter: owned ? 'none' : 'grayscale(100%)',
            transition: 'filter 0.3s'
          }}
        >
          {currentFrame ? (
            // With frame - wrap avatar in frame container
            <Box
              sx={{
                display: 'inline-flex',
                borderRadius: '50%',
                padding: currentFrame.requiredBadges >= 15 ? '4px' : currentFrame.requiredBadges >= 10 ? '3px' : '2px',
                width: '100%',
                height: '100%',
                ...frameStyle,
                transition: 'all 0.3s'
              }}
            >
              <Avatar
                src={avatar.imageUrl}
                alt={avatar.name}
                sx={{
                  width: '100%',
                  height: '100%',
                  bgcolor: 'primary.main',
                  fontSize: '1.2rem',
                  border: 'none'
                }}
              >
                {avatar.imageUrl ? null : (avatar.icon || '👤')}
              </Avatar>
            </Box>
          ) : (
            // Without frame - original display
            <Box
              className="avatar-image-container"
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {avatar.imageUrl ? (
                <img 
                  src={avatar.imageUrl} 
                  alt={avatar.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.nextElementSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: avatar.imageUrl ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3rem'
                }}
              >
                {avatar.icon || '👤'}
              </Box>
            </Box>
          )}
        </Box>
        
        {!hideName && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: owned ? 'text.primary' : 'text.secondary',
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {avatar.name}
          </Typography>
        )}
        
        <Chip
          label={color.label}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            bgcolor: color.bg,
            color: color.border,
            border: `1px solid ${color.border}`
          }}
        />
      </Paper>
    </Tooltip>
  );
}

export default AvatarCard;
